import React, { memo, useMemo, useRef, useState } from 'react';
import { Button, Dropdown } from 'antd';

import HeaderFilter from './HeaderFilter';
import { ARROW_DOWN, ARROW_UP, CLOSE, EYE_SLASH, FILTER, KEBAB, SNOWFLAKE } from '../icons';

const ARROW = { asc: '↑', desc: '↓' };

// A menu row that says why it is off: the label, and the reason under it.
const withReason = (label, reason) => (reason ? (
	<span className="column-menu__item">
		<span>{label}</span>
		<span className="column-menu__reason">{reason}</span>
	</span>
) : label);

/**
 * The menu behind a column header's ⋯ (KPR-1638). Three groups: the order,
 * the filter, the column itself. Every row is always drawn that the column
 * could ever have; one that cannot act now is disabled and says why beside its
 * label, so a person learns what the column can do, and what it cannot.
 *
 * `model` is `useTableHeaders`' answer for this column:
 * `{ label, sort: { field, reason, state, canClear }, filter, arrange: null |
 * { hide, freeze } }`, where `hide` is `null` (no Hide for this column) or
 * `{ reason }` and `freeze` is `null` or `{ frozen, reason }`. `filter` is
 * `{ mode, reason, active, column, read }`: mode 'header' (this header filters
 * the column), 'builder' (the builder does: the row says why and opens it) or
 * 'off' (no filter; the row is disabled with its reason).
 */
export function columnMenuItems(model) {
	const { label, sort, filter, arrange } = model;
	const items = [];
	if (sort.field) {
		items.push(
			{ key: 'sort-asc', icon: <ARROW_UP aria-hidden="true" />, label: 'Sort ascending' },
			{ key: 'sort-desc', icon: <ARROW_DOWN aria-hidden="true" />, label: 'Sort descending' },
			{
				key: 'sort-clear',
				icon: <CLOSE aria-hidden="true" />,
				label: withReason('Clear sort', sort.canClear ? null : `${label} is not the sort.`),
				disabled: !sort.canClear,
			},
		);
	} else {
		items.push({ key: 'sort-none', label: withReason('Sort', sort.reason), title: sort.reason, disabled: true });
	}
	items.push(
		{ type: 'divider', key: 'd-filter' },
		{
			key: 'filter',
			icon: <FILTER aria-hidden="true" />,
			label: withReason('Filter…', filter.reason),
			title: filter.reason,
			disabled: filter.mode === 'off',
		},
	);
	if (arrange && (arrange.hide || arrange.freeze)) {
		items.push({ type: 'divider', key: 'd-column' });
		if (arrange.hide) {
			items.push({
				key: 'hide',
				icon: <EYE_SLASH aria-hidden="true" />,
				label: withReason('Hide column', arrange.hide.reason),
				disabled: Boolean(arrange.hide.reason),
			});
		}
		if (arrange.freeze) {
			items.push({
				key: arrange.freeze.frozen ? 'unfreeze' : 'freeze',
				icon: <SNOWFLAKE aria-hidden="true" />,
				label: withReason(arrange.freeze.frozen ? 'Unfreeze column' : 'Freeze column', arrange.freeze.reason),
				disabled: Boolean(arrange.freeze.reason),
			});
		}
	}
	return items;
}

/**
 * The funnel's accessible name: what it filters, and whether the column is
 * filtered now ("on") or not ("off"), and where it is edited when the header
 * cannot show the condition.
 */
export function funnelName(label, filter) {
	const name = `Filter ${label || 'column'}: ${filter.active ? 'on' : 'off'}`;
	return filter.mode === 'builder' ? `${name}. ${filter.reason}` : name;
}

/**
 * A column's funnel (KPR-1639): filled while the column has a top-level
 * condition, an outline otherwise (shown with the ⋯, on hover and focus). On
 * a phone only a filled one is drawn: the ⋯ menu's Filter… is the way in.
 * The header's filter opens under it; a 'builder' column's opens the builder.
 */
function funnelFor({ columnKey, label, filter, isMobile, open, session, setOpen, openFilter, onFilter, funnelRef, returnFocus }) {
	if (filter.mode === 'off') return null;
	const name = funnelName(label, filter);
	const button = (isMobile && !filter.active) ? null : (
		<Button
			ref={funnelRef}
			type="text"
			size="small"
			className={`column-header__filter${filter.active ? ' column-header__filter--on' : ''}`}
			icon={<FILTER aria-hidden="true" />}
			aria-label={name}
			title={name}
			aria-haspopup={filter.mode === 'header' ? 'dialog' : undefined}
			aria-expanded={filter.mode === 'header' ? open : undefined}
			onClick={filter.mode === 'header' ? (isMobile ? openFilter : undefined) : openFilter}
		/>
	);
	if (filter.mode !== 'header') return button;
	return (
		<HeaderFilter
			open={open}
			session={session}
			onOpenChange={setOpen}
			onDone={returnFocus}
			isMobile={isMobile}
			column={filter.column}
			label={label || 'column'}
			read={filter.read}
			onApply={draft => onFilter(columnKey, 'apply', draft)}
			onClear={() => onFilter(columnKey, 'clear')}
		>
			{button || <span className="column-header__filter-anchor" />}
		</HeaderFilter>
	);
}

/**
 * A table column's header on the items page (KPR-1638, KPE-051 ruling i04): its
 * label — a button that cycles the column's sort when it can sort — with the
 * arrow of the order it is in, and a ⋯ that opens the column's menu. The ⋯
 * keeps its place at rest and shows on hover, on focus and always on touch
 * (css/components/table-header.css), so a header never reflows when the
 * pointer arrives. `aria-sort` is on the `th`, set by `useTableHeaders`.
 *
 * `onSort(key, mode)` takes 'cycle' | 'asc' | 'desc' | 'clear',
 * `onArrange(key, change)` 'hide' | 'freeze' | 'unfreeze' and
 * `onFilter(key, action, draft)` 'apply' | 'clear' | 'builder'; each is one
 * stable function for the whole table, so a header redraws only when its own
 * model changes.
 */
function ColumnHeader({ columnKey, model, align, isMobile = false, onSort, onArrange, onFilter, children }) {
	const { label, sort, filter } = model;
	const [filterOpen, setOpenState] = useState(false);
	// Each opening is a fresh panel (its key): the popover keeps its last
	// content while it fades, and a kept panel would reopen on an old draft.
	const [filterSession, setFilterSession] = useState(0);
	const setFilterOpen = (next) => {
		if (next && !filterOpen) setFilterSession(n => n + 1);
		setOpenState(next);
	};
	const funnelRef = useRef(null);
	const moreRef = useRef(null);
	// Back to the funnel, or the ⋯ where no funnel is drawn (a phone).
	const returnFocus = () => setTimeout(() => (funnelRef.current || moreRef.current)?.focus?.(), 0);
	const items = useMemo(() => columnMenuItems(model), [model]);
	// The header's filter, or the builder where the header cannot show it.
	const openFilter = () => (filter.mode === 'header' ? setFilterOpen(true) : onFilter(columnKey, 'builder'));
	const onClick = ({ key, domEvent }) => {
		domEvent?.stopPropagation?.();
		if (key.startsWith('sort-')) onSort(columnKey, key.slice('sort-'.length));
		else if (key === 'filter') openFilter();
		else onArrange(columnKey, key);
	};
	const arrow = sort.state ? <span className="column-header__arrow" aria-hidden="true" data-sort={sort.state}>{ARROW[sort.state]}</span> : null;
	const nextWords = sort.state === 'asc' ? 'descending' : sort.state === 'desc' && sort.willClear ? 'by the default order' : 'ascending';

	return (
		<div className={`column-header column-header--${align || 'left'}`} data-column-header={columnKey}>
			{sort.field ? (
				<button
					type="button"
					className="column-header__label column-header__label--sortable"
					onClick={() => onSort(columnKey, 'cycle')}
					title={`Sort ${nextWords}`}
				>
					<span className="column-header__text">{children}</span>
					{arrow}
				</button>
			) : (
				<span className="column-header__label">
					<span className="column-header__text">{children}</span>
				</span>
			)}
			{funnelFor({ columnKey, label, filter, isMobile, open: filterOpen, session: filterSession, setOpen: setFilterOpen, openFilter, onFilter, funnelRef, returnFocus })}
			<Dropdown trigger={['click']} placement="bottomRight" menu={{ items, onClick, className: 'column-menu' }}>
				<Button
					ref={moreRef}
					type="text"
					size="small"
					className="column-header__more"
					icon={<KEBAB aria-hidden="true" />}
					aria-label={`${label || 'Column'} column menu`}
					aria-haspopup="menu"
				/>
			</Dropdown>
		</div>
	);
}

export default memo(ColumnHeader);
